/**
 * Act 3 Store — 第三幕：关系考验
 *
 * 管理第三幕的周数、考验卡、策略选择、三维指标、叙事日志等状态。
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { TrialCard, StrategyId } from '@shared/types/cards';
import type { Act2Result } from '@shared/types/gameState';
import {
  INITIAL_STABILITY,
  ENERGY_MAX,
  ENERGY_RECOVERY_WEEKLY,
  INITIAL_COMMITMENT,
  HOT_ENTRY_COMMITMENT_BONUS,
  HOT_ENTRY_STABILITY_PENALTY,
  COLD_ENTRY_COMMITMENT_PENALTY,
  COLD_ENTRY_STABILITY_BONUS,
} from '@shared/constants/gameBalance';
import { drawTrialCards } from '@shared/constants/act3Trials';

type Act3Phase = 'WAITING' | 'TRIAL_REVEAL' | 'STRATEGY_SELECT' | 'PROCESSING' | 'WEEK_RESULT' | 'FINISHED';

interface Act3StoreState {
  week: number;
  phase: Act3Phase;

  // 三维指标
  commitmentA: number;
  commitmentB: number;
  energyA: number;
  energyB: number;
  stability: number;

  // 考验系统
  trials: TrialCard[];
  activeTrialIndex: number;
  hiddenTrialRevealed: boolean;

  // 策略
  chosenStrategy: StrategyId | null;
  usedSpecialStrategies: StrategyId[];

  // 叙事
  narrativeLog: string[];
  weekNarrative: string;

  // 第二幕入场类型
  entryType: string;

  // 瞬态 UI
  isProcessing: boolean;

  // 双视角内心独白（流式）
  innerMonologueA: string;
  innerMonologueB: string;
  streamingMonologueSide: 'A' | 'B' | null;
  streamingMonologueText: string;
}

interface Act3StoreActions {
  initialize: (act2Result?: Act2Result) => void;

  // 周
  nextWeek: () => void;
  setPhase: (phase: Act3Phase) => void;

  // 指标
  adjustCommitment: (side: 'A' | 'B', delta: number) => void;
  adjustEnergy: (side: 'A' | 'B', delta: number) => void;
  adjustStability: (delta: number) => void;
  applyWeeklyRecovery: () => void;

  // 考验
  advanceTrial: () => void;
  revealHiddenTrial: (trial: TrialCard) => void;

  // 策略
  chooseStrategy: (strategy: StrategyId) => void;
  markSpecialUsed: (strategy: StrategyId) => void;

  // 叙事
  addNarrative: (text: string) => void;
  setWeekNarrative: (text: string) => void;

  // UI
  setIsProcessing: (v: boolean) => void;

  // 双视角内心独白
  setInnerMonologue: (side: 'A' | 'B', text: string) => void;
  appendStreamingMonologue: (text: string) => void;
  setStreamingMonologueSide: (side: 'A' | 'B' | null) => void;
  clearStreamingMonologue: () => void;
  commitMonologue: () => void;

  // 重置
  reset: () => void;
}

export type Act3Store = Act3StoreState & Act3StoreActions;

const INITIAL_STATE: Act3StoreState = {
  week: 1,
  phase: 'WAITING',
  commitmentA: INITIAL_COMMITMENT,
  commitmentB: INITIAL_COMMITMENT,
  energyA: ENERGY_MAX,
  energyB: ENERGY_MAX,
  stability: INITIAL_STABILITY,
  trials: [],
  activeTrialIndex: 0,
  hiddenTrialRevealed: false,
  chosenStrategy: null,
  usedSpecialStrategies: [],
  narrativeLog: [],
  weekNarrative: '',
  entryType: '',
  isProcessing: false,
  innerMonologueA: '',
  innerMonologueB: '',
  streamingMonologueSide: null,
  streamingMonologueText: '',
};

function clamp(val: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, val));
}

export const useAct3Store = create<Act3Store>()(
  persist(
    (set, get) => ({
      ...INITIAL_STATE,

      initialize: (act2Result) => {
        // 根据第二幕结局确定初始值
        let commitmentBonus = 0;
        let stabilityBonus = 0;
        let entryType = '暧昧入场';

        if (act2Result) {
          entryType = act2Result.outcome;
          commitmentBonus = act2Result.commitmentBonus ?? 0;
          stabilityBonus = act2Result.stabilityBonus ?? 0;

          // 入场类型加成
          switch (act2Result.outcome) {
            case '热恋入场':
              commitmentBonus += HOT_ENTRY_COMMITMENT_BONUS;
              stabilityBonus -= HOT_ENTRY_STABILITY_PENALTY;
              break;
            case '冷静入场':
              commitmentBonus -= COLD_ENTRY_COMMITMENT_PENALTY;
              stabilityBonus += COLD_ENTRY_STABILITY_BONUS;
              break;
          }
        }

        const baseCommitment = INITIAL_COMMITMENT + commitmentBonus;
        const baseStability = INITIAL_STABILITY + stabilityBonus;

        // 抽取2张考验卡
        const trials = drawTrialCards(2);

        set({
          ...INITIAL_STATE,
          commitmentA: clamp(baseCommitment, 0, 100),
          commitmentB: clamp(baseCommitment, 0, 100),
          stability: clamp(baseStability, 0, 100),
          trials,
          entryType,
        });
      },

      nextWeek: () => set(s => ({ week: s.week + 1 })),
      setPhase: (phase) => set({ phase }),

      adjustCommitment: (side, delta) => set(s => {
        const key = side === 'A' ? 'commitmentA' : 'commitmentB';
        return { [key]: clamp(s[key] + delta, 0, 100) };
      }),

      adjustEnergy: (side, delta) => set(s => {
        const key = side === 'A' ? 'energyA' : 'energyB';
        return { [key]: clamp(s[key] + delta, 0, ENERGY_MAX) };
      }),

      adjustStability: (delta) => set(s => ({
        stability: clamp(s.stability + delta, 0, 100),
      })),

      applyWeeklyRecovery: () => set(s => ({
        energyA: clamp(s.energyA + ENERGY_RECOVERY_WEEKLY, 0, ENERGY_MAX),
        energyB: clamp(s.energyB + ENERGY_RECOVERY_WEEKLY, 0, ENERGY_MAX),
      })),

      advanceTrial: () => set(s => ({
        activeTrialIndex: Math.min(s.activeTrialIndex + 1, s.trials.length - 1),
        chosenStrategy: null,
      })),

      revealHiddenTrial: (trial) => set(s => ({
        trials: [...s.trials, trial],
        hiddenTrialRevealed: true,
      })),

      chooseStrategy: (strategy) => set({ chosenStrategy: strategy }),

      markSpecialUsed: (strategy) => set(s => ({
        usedSpecialStrategies: [...s.usedSpecialStrategies, strategy],
      })),

      addNarrative: (text) => set(s => ({
        narrativeLog: [...s.narrativeLog, text],
      })),

      setWeekNarrative: (text) => set({ weekNarrative: text }),

      setIsProcessing: (v) => set({ isProcessing: v }),

      // 双视角内心独白
      setInnerMonologue: (side, text) => set(side === 'A' ? { innerMonologueA: text } : { innerMonologueB: text }),
      appendStreamingMonologue: (text) => set(s => ({ streamingMonologueText: s.streamingMonologueText + text })),
      setStreamingMonologueSide: (side) => set({ streamingMonologueSide: side, streamingMonologueText: '' }),
      clearStreamingMonologue: () => set({ streamingMonologueSide: null, streamingMonologueText: '' }),
      commitMonologue: () => set(s => {
        if (!s.streamingMonologueSide || !s.streamingMonologueText) return s;
        const key = s.streamingMonologueSide === 'A' ? 'innerMonologueA' : 'innerMonologueB';
        return {
          [key]: s.streamingMonologueText,
          streamingMonologueSide: null,
          streamingMonologueText: '',
        };
      }),

      reset: () => set({ ...INITIAL_STATE }),
    }),
    {
      name: 'cm-v2-act3',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        week: state.week,
        phase: state.phase,
        commitmentA: state.commitmentA,
        commitmentB: state.commitmentB,
        energyA: state.energyA,
        energyB: state.energyB,
        stability: state.stability,
        trials: state.trials,
        activeTrialIndex: state.activeTrialIndex,
        hiddenTrialRevealed: state.hiddenTrialRevealed,
        chosenStrategy: state.chosenStrategy,
        usedSpecialStrategies: state.usedSpecialStrategies,
        narrativeLog: state.narrativeLog,
        weekNarrative: state.weekNarrative,
        entryType: state.entryType,
        innerMonologueA: state.innerMonologueA,
        innerMonologueB: state.innerMonologueB,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        state.isProcessing = false;
        state.streamingMonologueSide = null;
        state.streamingMonologueText = '';
      },
    },
  ),
);
